<template>
  <q-page class="about-page">
    <KdsNav
      :logo-label="t('app.name')"
      :links="navLinks"
      :cta-secondary="t('landing.nav.logIn')"
      @cta-secondary-click="goAuth"
    />

    <div class="about-page__inner">
      <!-- Block 1 — Our story -->
      <section class="about-page__block" data-reveal>
        <KdsEyebrow>{{ t('about.story.eyebrow') }}</KdsEyebrow>
        <h1 class="about-page__title">{{ t('about.story.title') }}</h1>
        <p
          v-for="(paragraph, index) in tm('about.story.paragraphs')"
          :key="index"
          class="about-page__body"
        >
          {{ rt(paragraph) }}
        </p>
      </section>

      <hr class="about-page__separator" />

      <!-- Block 2 — Why Korum -->
      <section class="about-page__block" data-reveal>
        <KdsEyebrow>{{ t('about.whyKorum.eyebrow') }}</KdsEyebrow>
        <h2 class="about-page__section-title">{{ t('about.whyKorum.title') }}</h2>
        <p
          v-for="(paragraph, index) in tm('about.whyKorum.paragraphs')"
          :key="index"
          class="about-page__body"
        >
          {{ rt(paragraph) }}
        </p>
      </section>

      <p class="about-page__tagline">{{ t('about.tagline') }}</p>

      <hr class="about-page__separator" />

      <!-- Block 3 — Contact -->
      <section id="contact" class="about-page__block about-page__contact" data-reveal>
        <KdsEyebrow>{{ t('about.contact.eyebrow') }}</KdsEyebrow>
        <h2 class="about-page__section-title">{{ t('about.contact.title') }}</h2>
        <p class="about-page__subtitle">{{ t('about.contact.subtitle') }}</p>
        <ContactForm />
      </section>
    </div>

    <KdsFooter
      :brand="t('app.name')"
      :status-label="t('landing.footer.status')"
      :status-to="'/status'"
      :columns="footerColumns"
    />
  </q-page>
</template>

<script setup lang="ts">
import { computed, onMounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import KdsNav from '@/kds/components/KdsNav.vue';
import KdsFooter from '@/kds/components/KdsFooter.vue';
import KdsEyebrow from '@/kds/primitives/KdsEyebrow.vue';
import ContactForm from '@/components/about/ContactForm.vue';
import { useScrollToAnchor } from '@/composables/useScrollToAnchor';
import { useRevealOnScroll } from '@/composables/useRevealOnScroll';
import { useConsent } from '@/composables/useConsent';
import { useSeoMeta } from '@/composables/useSeoMeta';

const { t, tm, rt } = useI18n();

useSeoMeta('about');
const router = useRouter();
const route = useRoute();
useRevealOnScroll();
const { scrollToHash } = useScrollToAnchor();
const { openPreferences } = useConsent();

const navLinks = computed(() => [
  { label: t('landing.nav.product'), href: '/#how' },
  { label: t('landing.nav.protocols'), href: '/#protocols' },
  { label: t('landing.nav.pricing'), href: '/#pricing' },
  { label: t('landing.nav.docs'), href: '/docs' },
  { label: t('landing.nav.changelog'), href: '/changelogs' }
]);

const footerColumns = computed(() => [
  {
    title: t('landing.footer.productTitle'),
    links: [
      { label: t('landing.footer.linkOverview'), href: '/#how' },
      { label: t('landing.footer.linkProtocols'), href: '/#protocols' },
      { label: t('landing.footer.linkPricing'), href: '/#pricing' }
    ]
  },
  {
    title: t('landing.footer.resourcesTitle'),
    links: [
      { label: t('landing.footer.linkDocs'), to: '/docs' },
      { label: t('landing.footer.linkChangelog'), to: '/changelogs' },
      { label: t('landing.footer.linkStatus'), to: '/status' }
    ]
  },
  {
    title: t('landing.footer.companyTitle'),
    links: [
      { label: t('landing.footer.linkAbout'), to: '/about' },
      { label: t('landing.footer.linkContact'), to: { path: '/about', hash: '#contact' } }
    ]
  },
  {
    title: t('landing.footer.legalTitle'),
    links: [
      { label: t('landing.footer.linkPrivacy'), to: '/privacy' },
      { label: t('landing.footer.linkTerms'), to: '/terms' },
      { label: t('landing.footer.linkCookies'), to: '/cookies' },
      { label: t('landing.footer.linkLegalNotice'), to: '/legal-notice' },
      { label: t('landing.footer.manageCookies'), action: openPreferences }
    ]
  }
]);

const goAuth = (): void => {
  void router.push({ name: 'auth' });
};

onMounted(async () => {
  if (route.hash) {
    await nextTick();
    scrollToHash(route.hash);
  }
});
</script>

<style scoped lang="scss">
.about-page {
  background: var(--paper);
  color: var(--ink);

  &__inner {
    max-width: 800px;
    margin: 0 auto;
    padding: var(--space-11) var(--space-7);
  }

  &__block {
    display: flex;
    flex-direction: column;
    gap: var(--space-5);
  }

  &__contact {
    scroll-margin-top: calc(var(--nav-height, 56px) + var(--space-2));
  }

  &__title {
    margin: 0;
    font-family: var(--font-display);
    font-size: var(--fs-display-m);
    line-height: 1.08;
    letter-spacing: -0.02em;
    color: var(--ink);
  }

  &__section-title {
    margin: 0;
    font-family: var(--font-display);
    font-size: var(--fs-h1);
    line-height: 1.18;
    letter-spacing: -0.015em;
    color: var(--ink);
  }

  &__body {
    margin: 0;
    font-size: var(--fs-body);
    line-height: 1.65;
    color: var(--stone);
    max-width: 640px;
  }

  &__subtitle {
    margin: 0;
    font-size: var(--fs-body);
    color: var(--stone);
  }

  &__separator {
    border: none;
    border-top: var(--border);
    margin-top: var(--space-9);
    margin-bottom: var(--space-9);
  }

  &__tagline {
    margin: var(--space-5) 0 0;
    font-size: var(--fs-body-sm);
    color: var(--stone-2);
    font-style: italic;
  }
}
</style>
